import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { raceApi } from '../utils/api';

export function PredictionPanel({ onPredict }) {
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchStatus, setFetchStatus] = useState('');

  useEffect(() => {
    loadTodayRaces();
  }, []);

  const loadTodayRaces = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 レースデータを取得中...');
      const data = await raceApi.getTodayRaces();
      console.log('📊 取得されたレースデータ:', data);
      
      if (data.races && data.races.length > 0) {
        console.log(`✅ ${data.races.length}レースを取得`);
        setRaces(data.races);
      } else {
        console.warn('⚠️ レースデータが空です', data);
        setError(`レースデータが見つかりません: ${data.message || '不明なエラー'}`);
        setRaces([]);
      }
    } catch (err) {
      console.error('❌ レース取得エラー:', err);
      setError(`実データ取得に失敗: ${err.message} - サンプルデータは使用しません`);
      setRaces([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePredict = async () => {
    if (!selectedRace) return;

    try {
      setPredicting(true);
      setError(null);
      
      console.log('🏇 予測実行:', selectedRace.race_id);
      
      // 実際の馬データを使用
      const horses = selectedRace.horses || [];
      console.log(`📊 馬データ: ${horses.length}頭`, horses);
      
      if (horses.length === 0) {
        throw new Error('馬データが見つかりません');
      }
      
      const result = await onPredict(selectedRace.race_id, horses);
      console.log('✅ 予測結果:', result);
      setPrediction(result);
    } catch (err) {
      console.error('❌ 予測エラー:', err);
      setError(`予測に失敗: ${err.message}`);
    } finally {
      setPredicting(false);
    }
  };

  const handleFetchTodayRaces = async () => {
    try {
      setFetching(true);
      setFetchStatus('今日のレース情報を取得中...');
      setError(null);

      console.log('🔄 バックエンドにレース情報取得を要求');

      // バックエンドに取得要求を送信
      const response = await fetch('https://keiba-prediction-api.riho-dare.workers.dev/api/fetch-today', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_refresh: true })
      });

      const result = await response.json();

      if (result.success) {
        setFetchStatus(result.message);

        // ステータスをポーリングして完了を待つ
        const today = new Date().toISOString().slice(0, 10);
        let attempts = 0;
        const maxAttempts = 30; // 最大3分待機

        const pollStatus = async () => {
          try {
            const statusResponse = await fetch(`https://keiba-prediction-api.riho-dare.workers.dev/api/fetch-status/${today}`);
            const statusData = await statusResponse.json();

            setFetchStatus(`${statusData.message} (${statusData.races_count}レース)`);

            if (statusData.status === 'completed' && statusData.races_count > 0) {
              setFetchStatus('✅ レース情報取得完了！データを再読み込み中...');
              
              // データ取得完了後、レース一覧を再読み込み
              setTimeout(() => {
                loadTodayRaces();
                setFetching(false);
                setFetchStatus('');
              }, 1000);
              
              return;
            } else if (attempts < maxAttempts) {
              attempts++;
              setTimeout(pollStatus, 6000); // 6秒間隔でポーリング
            } else {
              setFetchStatus('⏰ タイムアウト：手動でページを更新してください');
              setFetching(false);
            }
          } catch (err) {
            console.error('ステータス確認エラー:', err);
            setFetchStatus('❌ ステータス確認に失敗');
            setFetching(false);
          }
        };

        // 初回ステータスチェックを1秒後に開始
        setTimeout(pollStatus, 1000);

      } else {
        setError(`取得要求失敗: ${result.message}`);
        setFetching(false);
        setFetchStatus('');
      }

    } catch (err) {
      console.error('❌ レース取得要求エラー:', err);
      setError(`レース情報取得要求に失敗: ${err.message}`);
      setFetching(false);
      setFetchStatus('');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>レース予測</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">レース情報を読み込み中...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* 今日のレース情報取得ボタン */}
              <div className="flex gap-2">
                <button
                  onClick={handleFetchTodayRaces}
                  disabled={fetching}
                  className="flex-1 btn-secondary"
                >
                  {fetching ? (
                    <>
                      <div className="spinner mr-2"></div>
                      取得中...
                    </>
                  ) : (
                    '📅 今日のレース情報を取得'
                  )}
                </button>
                <button
                  onClick={loadTodayRaces}
                  disabled={loading || fetching}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  🔄
                </button>
              </div>

              {/* 取得ステータス表示 */}
              {fetchStatus && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">{fetchStatus}</p>
                </div>
              )}

              {/* レース選択 */}
              <div>
                <label htmlFor="race-select" className="block text-sm font-medium text-gray-700 mb-2">
                  レースを選択
                </label>
                <select
                  id="race-select"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={selectedRace?.race_id || ''}
                  onChange={(e) => {
                    const race = races.find(r => r.race_id === e.target.value);
                    setSelectedRace(race);
                    setPrediction(null);
                  }}
                >
                  <option value="">レースを選択してください</option>
                  {races.map(race => (
                    <option key={race.race_id} value={race.race_id}>
                      {race.place} {race.race_num}R - {race.race_name} ({race.horse_count}頭立て)
                      {race.has_prediction && ' ✓'}
                    </option>
                  ))}
                </select>
              </div>

              {/* 予測ボタン */}
              {selectedRace && (
                <button
                  onClick={handlePredict}
                  disabled={predicting}
                  className="w-full btn-primary"
                >
                  {predicting ? (
                    <>
                      <div className="spinner mr-2"></div>
                      予測中...
                    </>
                  ) : (
                    '予測を実行'
                  )}
                </button>
              )}

              {/* エラー表示 */}
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 予測結果 */}
      {prediction && (
        <Card className="slide-in">
          <CardHeader>
            <CardTitle>予測結果</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* 推奨ベット */}
              {prediction.recommended_bets && prediction.recommended_bets.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  推奨ベット（期待値順）
                </h4>
                <div className="space-y-2">
                  {prediction.recommended_bets.map((bet, index) => (
                    <div
                      key={bet.horse_num}
                      className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-green-700">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">
                            {bet.horse_num}番 {bet.horse_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            勝率 {(bet.win_prob * 100).toFixed(1)}% / オッズ {bet.odds.toFixed(1)}倍
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-700">
                          期待値 {bet.expected_value.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">
                          推奨 {bet.bet_amount.toLocaleString()}円
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* 全予測一覧 */}
              {prediction.predictions && prediction.predictions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  全出走馬の評価
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          馬番
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          馬名
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          勝率
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          オッズ
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          期待値
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {prediction.predictions.map((p, index) => (
                        <tr key={p.horse_num || index} className={(p.expected_value || 0) > 1.2 ? 'bg-green-50' : ''}>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {p.horse_num || '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {p.horse_name || `馬${p.horse_num || index + 1}`}
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-900">
                            {((p.win_probability || p.win_prob || 0) * 100).toFixed(1)}%
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-900">
                            {(p.odds || 0).toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-sm text-right font-medium">
                            <span className={(p.expected_value || 0) > 1.2 ? 'text-green-600' : 'text-gray-900'}>
                              {(p.expected_value || 0).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* メタ情報 */}
              <div className="text-xs text-gray-500 text-right">
                モデル: {prediction.model_version || 'Unknown'} | 
                予測時刻: {new Date(prediction.timestamp || Date.now()).toLocaleTimeString('ja-JP')}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// サンプル・デモデータ生成機能は完全に削除
// 実データのみ使用